import { useState, useEffect, useRef } from 'react';
import { DEFAULT_COIN_ICON } from '../utils/helpers';
import { API_BASE_URL } from '../utils/apiConfig';

/**
 * Custom hook to fetch top N trades from MongoDB (entire database history)
 * Fetched ONCE on page load - no real-time updates needed
 *
 * @param {string} type - 'whale_trade' or 'liquidation'
 * @param {number} limit - Number of top trades to fetch (default: 3)
 */
export function useTopTrades(type = 'whale_trade', limit = 3) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [topTrades, setTopTrades] = useState([]);
  const symbolsCacheRef = useRef({});

  // Load symbols cache on mount - API first, JSON fallback
  useEffect(() => {
    const loadSymbolsCache = async () => {
      try {
        // Try API first (fresh data from MongoDB)
        const apiResponse = await fetch(`${API_BASE_URL}/api/icons/all`);
        if (apiResponse.ok) {
          const data = await apiResponse.json();
          symbolsCacheRef.current = data;
          console.log('✅ [useTopTrades] Icons loaded from API:', Object.keys(data).length, 'symbols');
          return;
        }
      } catch (error) {
        console.warn('⚠️ [useTopTrades] API failed, falling back to JSON:', error.message);
      }

      // Fallback to static JSON
      try {
        const response = await fetch(`${API_BASE_URL}/mapped_symbols.json`);
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        const data = await response.json();
        symbolsCacheRef.current = data;
        console.log('✅ [useTopTrades] Icons loaded from JSON fallback:', Object.keys(data).length, 'symbols');
      } catch (error) {
        console.error('❌ [useTopTrades] Error loading symbols cache:', error);
        symbolsCacheRef.current = {};
      }
    };
    loadSymbolsCache();
  }, []);

  // Get symbol logo from cache or API
  const getSymbolLogo = async (symbol) => {
    const normalizedSymbol = symbol.toUpperCase().replace(/[-_]|SWAP|FUTURES/gi, '').replace(/USD$/i, 'USDT');

    // Check cache first
    if (symbolsCacheRef.current[normalizedSymbol]) {
      return symbolsCacheRef.current[normalizedSymbol];
    }

    // Fetch from API
    try {
      const response = await fetch(`${API_BASE_URL}/api/icon/${encodeURIComponent(normalizedSymbol)}`);
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.iconUrl) {
          // Store in cache for future use
          symbolsCacheRef.current[normalizedSymbol] = data.iconUrl;
          return data.iconUrl;
        }
      }
    } catch (error) {
      console.error(`❌ [useTopTrades] Error fetching icon for ${normalizedSymbol}:`, error);
    }

    // Fallback to default icon
    return DEFAULT_COIN_ICON;
  };

  useEffect(() => {
    let isMounted = true;

    const fetchTopTrades = async () => {
      try {
        console.log(`⭐ [useTopTrades] Fetching top ${limit} ${type}s from database...`);

        const response = await fetch(`${API_BASE_URL}/api/top-trades?type=${type}&limit=${limit}`);

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        if (!isMounted) return;

        if (data.success && data.trades) {
          console.log(`✅ [useTopTrades] Loaded ${data.trades.length} top ${type}s:`,
            data.trades.map(t => `${t.symbol} $${(t.dollarSize / 1000).toFixed(0)}K`).join(', ')
          );

          // Enrich trades with logos
          const enrichedTrades = await Promise.all(data.trades.map(async (trade) => {
            const logoUrl = await getSymbolLogo(trade.symbol);
            return { ...trade, logoUrl };
          }));

          if (isMounted) {
            setTopTrades(enrichedTrades);
            setError(null);
          }
        } else {
          throw new Error(data.error || 'Failed to fetch top trades');
        }
      } catch (err) {
        console.error(`❌ [useTopTrades] Error fetching top ${type}s:`, err);
        if (isMounted) {
          setError(err.message);
          setTopTrades([]);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchTopTrades();

    return () => {
      isMounted = false;
    };
  }, [type, limit]); // Re-fetch if type or limit changes

  return { loading, error, topTrades };
}
